import { useEffect, useState } from 'react';
import { useChatContext } from '../store/ChatContext';

/**
 * Live countdown for the per-session message quota (`bot:error` with
 * `code: 'rate_limited'`, 20 messages/minute on the BE).
 *
 * Deliberately the same shape as the host's useNewSessionCooldown (per-IP
 * new-session quota) so the widget can feed either one into the same disabled
 * composer + "please wait {{time}}" placeholder — but it lives in bot-core and
 * keeps no storage: this deadline arrives over the socket, belongs to the
 * session on screen, and must not outlive the tab.
 *
 * Call it ONCE per widget — it runs an interval.
 */
export function useSendCooldown(): {
  /** True while the BE would reject the next message. */
  active: boolean;
  secondsLeft: number;
} {
  const { state, dispatch } = useChatContext();
  const until = state.sendCooldownUntil;
  const [secondsLeft, setSecondsLeft] = useState(0);

  useEffect(() => {
    if (!until) {
      setSecondsLeft(0);
      return;
    }
    const tick = () => {
      const remaining = Math.ceil((until - Date.now()) / 1000);
      if (remaining <= 0) {
        setSecondsLeft(0);
        // Clearing the deadline (rather than just reporting zero) keeps the
        // reducer the single source of truth — a re-mounted widget must not
        // resurrect an elapsed cooldown.
        dispatch({ type: 'SET_SEND_COOLDOWN', until: null });
        return;
      }
      setSecondsLeft(remaining);
    };
    tick(); // paint the first value immediately, don't wait 1s for it
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, [until, dispatch]);

  return { active: !!until && secondsLeft > 0, secondsLeft };
}
